import React from "react";
import { Button, Input } from "reactstrap";
import {
  Home,
  Edit2,
  Trash2,
  Monitor,
  UserPlus,
} from "react-feather";

/**
 * RoomTree - Renders a flat list of rooms with their devices and points.
 * Props:
 *   rooms: array of room objects
 *   expandedRooms: Set of expanded room IDs (kept for compatibility)
 *   onToggleRoom: (roomId) => void
 *   onEditRoom: (room) => void
 *   onDeleteRoom: (room) => void
 *   onCreateDevice: (room) => void
 *   onViewDevice: (device) => void
 *   onAssignUser: (room) => void
 */
const RoomTree = ({
  rooms,
  expandedRooms,
  onToggleRoom,
  onEditRoom,
  onDeleteRoom,
  onCreateDevice,
  onViewDevice,
  onAssignUser,
}) => {
  const renderRoomNode = (room, level = 0, isLast = false, parentIsLast = []) => {

    // Calculate left padding based on level
    const leftPadding = level * 32 + 12;

    return (
      <div key={room.id} style={{ position: "relative" }}>
        {/* Vertical and horizontal connector lines */}
        {level > 0 && (
          <>
            {/* Vertical line connecting to parent */}
            <div
              style={{
                position: "absolute",
                left: `${leftPadding - 20}px`,
                top: 0,
                bottom: isLast ? "50%" : 0,
                width: "1px",
                backgroundColor: "#cbd5e0",
              }}
            />
            {/* Horizontal line to the node */}
            <div
              style={{
                position: "absolute",
                left: `${leftPadding - 20}px`,
                top: "20px",
                width: "16px",
                height: "1px",
                backgroundColor: "#cbd5e0",
              }}
            />
          </>
        )}

        <div
          style={{
            paddingLeft: `${leftPadding}px`,
            paddingRight: "12px",
            paddingTop: "8px",
            paddingBottom: "8px",
            display: "flex",
            alignItems: "center",
            backgroundColor: level === 0 ? "#f8fafc" : "transparent",
            borderRadius: level === 0 ? "6px" : "0",
            marginBottom: "2px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => {
            if (level > 0) e.currentTarget.style.backgroundColor = "#f1f5f9";
          }}
          onMouseLeave={(e) => {
            if (level > 0) e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              cursor: "default",
              gap: "8px",
            }}
          >
            {/* Room icon with background */}
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                backgroundColor: level === 0 ? "#3b82f6" : "#60a5fa",
                color: "white",
              }}
            >
              <Home size={12} />
            </div>

            {/* Room name */}
            <span
              style={{
                fontWeight: level === 0 ? "600" : "500",
                color: "#1e293b",
                fontSize: level === 0 ? "0.95rem" : "0.9rem",
              }}
            >
              {room.name}
            </span>

            {/* Room ID badge */}
            <span
              style={{
                fontSize: "0.75rem",
                color: "#64748b",
                backgroundColor: "#f1f5f9",
                padding: "2px 8px",
                borderRadius: "4px",
                fontFamily: "monospace",
              }}
            >
              #{room.id}
            </span>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "6px" }}>
            <Button
              color="warning"
              size="sm"
              outline
              style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "0.85rem" }}
              onClick={(e) => {
                e.stopPropagation();
                onAssignUser(room);
              }}
              title="Assign user to room"
            >
              <UserPlus size={14} />
            </Button>
            <Button
              color="success"
              size="sm"
              outline
              style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "0.85rem" }}
              onClick={(e) => {
                e.stopPropagation();
                onCreateDevice(room);
              }}
              title="Create device"
            >
              <Monitor size={14} />
            </Button>
            <Button
              color="info"
              size="sm"
              outline
              style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "0.85rem" }}
              onClick={(e) => {
                e.stopPropagation();
                onEditRoom(room);
              }}
              title="Edit room"
            >
              <Edit2 size={14} />
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "0.85rem" }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRoom(room);
              }}
              title="Delete room"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        {/* Devices list */}
        {room.devices && room.devices.length > 0 && (
          <div style={{ position: "relative" }}>
            {room.devices.map((device, deviceIndex) => {
              const deviceLevel = level + 1;
              const deviceLeftPadding = deviceLevel * 32 + 12;
              const isLastDevice = deviceIndex === room.devices.length - 1;

              return (
                <div key={device.id} style={{ position: "relative" }}>
                  {/* Vertical and horizontal connector lines for device */}
                  <div
                    style={{
                      position: "absolute",
                      left: `${deviceLeftPadding - 20}px`,
                      top: 0,
                      bottom: isLastDevice ? "50%" : 0,
                      width: "1px",
                      backgroundColor: "#a7f3d0",
                    }}
                  />
                  {/* Horizontal line to the device */}
                  <div
                    style={{
                      position: "absolute",
                      left: `${deviceLeftPadding - 20}px`,
                      top: "18px",
                      width: "16px",
                      height: "1px",
                      backgroundColor: "#a7f3d0",
                    }}
                  />

                  <div
                    style={{
                      paddingLeft: `${deviceLeftPadding}px`,
                      paddingRight: "12px",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "transparent",
                      marginBottom: "2px",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                      borderRadius: "4px",
                    }}
                    onClick={() => onViewDevice(device)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f0fdf4";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {/* Device icon with background */}
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        backgroundColor: "#22c55e",
                        color: "white",
                        marginRight: "8px",
                      }}
                    >
                      <Monitor size={12} />
                    </div>

                    {/* Device name */}
                    <span
                      style={{ fontWeight: "500", color: "#166534", fontSize: "0.9rem" }}
                    >
                      {device.name}
                    </span>

                    {/* Device type and description */}
                    <span
                      style={{ marginLeft: "8px", fontSize: "0.75rem", color: "#15803d" }}
                    >
                      ({device.deviceTypeName}
                      {device.description && ` - ${device.description}`})
                    </span>

                    {/* Device ID badge */}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.7rem",
                        color: "#166534",
                        backgroundColor: "#d1fae5",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                      }}
                    >
                      #{device.id}
                    </span>
                  </div>

                  {/* Points list under device */}
                  {device.points && device.points.length > 0 && (
                    <div style={{ position: "relative" }}>
                      {device.points.map((point, pointIndex) => {
                        const pointLevel = deviceLevel + 1;
                        const pointLeftPadding = pointLevel * 32 + 12;
                        const isLastPoint = pointIndex === device.points.length - 1;

                        return (
                          <div key={point.id} style={{ position: "relative" }}>
                            {/* Vertical and horizontal connector lines for point */}
                            <div
                              style={{
                                position: "absolute",
                                left: `${pointLeftPadding - 20}px`,
                                top: 0,
                                bottom: isLastPoint ? "50%" : 0,
                                width: "1px",
                                backgroundColor: "#c4b5fd",
                              }}
                            />
                            {/* Horizontal line to the point */}
                            <div
                              style={{
                                position: "absolute",
                                left: `${pointLeftPadding - 20}px`,
                                top: "14px",
                                width: "16px",
                                height: "1px",
                                backgroundColor: "#c4b5fd",
                              }}
                            />

                            <div
                              style={{
                                paddingLeft: `${pointLeftPadding}px`,
                                paddingRight: "12px",
                                paddingTop: "4px",
                                paddingBottom: "4px",
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "transparent",
                                marginBottom: "1px",
                              }}
                            >
                              {/* Point icon */}
                              <div
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor: "#8b5cf6",
                                  marginRight: "8px",
                                  flexShrink: 0,
                                }}
                              />

                              {/* Point display name */}
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#6b21a8",
                                  fontWeight: "400",
                                }}
                              >
                                {point.pointDisplayName || point.pointId}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    );
  };

  return (
    <>
      {(rooms || []).map((room, index, array) =>
        renderRoomNode(room, 0, index === array.length - 1, [])
      )}
    </>
  );
};

export default RoomTree;
